import { Box, Button, Container, Stack, Typography, useMediaQuery } from '@mui/material';
import {
    DataGrid,
    GridColDef,
    GridColumnHeaderParams,
    GridRenderCellParams,
    GridRowsProp,
} from '@mui/x-data-grid';
import { Link } from 'react-router-dom';
import { AuthStatus, useAuth } from '../auth/Auth';
import { useEffect, useState } from 'react';
import { useApi } from '../api/Api';
import { RequestSnackbar, useRequest } from '../api/Request';
import { BandsintownEvent } from '../database/events';
import BandsInTownImage from '../assets/BandsInTown.svg';
import SocialBar from '../socials/SocialBar';
import LoadingPage from '../loading/LoadingPage';
import { useTheme } from '@mui/material/styles';
import { getConfig } from '../config';
import { LocalActivity, NotificationsActive } from '@mui/icons-material'

const EventsPage = () => {
    const auth = useAuth();
    const api = useApi();
    const request = useRequest<BandsintownEvent[]>();
    const theme = useTheme();
    const [rows, setRows] = useState<GridRowsProp>([]);
    const config = getConfig();
    const isSmallScreen = useMediaQuery('(max-width:900px)');

    const smallScreenColumns: GridColDef[] = [
        {
            field: 'eventDetails',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant="body2" sx={{fontFamily: 'LemonMilk'}}>
                    {'Event Details'}
                </Typography>
            ),
            flex: 1,
            renderCell: (params: GridRenderCellParams<any, any>) => (
                <div>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {new Date(params.row.id).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2">
                        {params.row.venueName}
                    </Typography>
                    <Typography variant="body2">
                        {params.row.venueLocation}
                    </Typography>
                </div>
            ),
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            disableColumnMenu: true,
            display: 'flex'
        },
        {
            field: 'links',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant="body2" sx={{fontFamily: 'LemonMilk'}}>
                    {'Links'}
                </Typography>
            ),            flex: 1,
            renderCell: (params: GridRenderCellParams<any, any>) => (
                <Stack spacing={1} alignItems='center'>
                    {params.row.ticketLink && (
                        <Button
                            variant="contained"
                            onClick={() => window.open(params.row.ticketLink, '_blank')}
                            startIcon={<LocalActivity />}
                            sx={{
                                minWidth: 140,
                            }}
                        >
                            Tickets
                        </Button>
                    )}
                    {params.row.rsvpLink && (
                        <Button
                            variant="contained"
                            onClick={() =>
                                window.open(
                                    `https://bandsintown.com/artist-rsvp/2619661?event_id=${params.row.rsvpLink}`,
                                    '_blank'
                                )
                            }
                            startIcon={<NotificationsActive />}
                            sx={{
                                minWidth: 140,
                            }}
                        >
                            Remind Me
                        </Button>
                    )}
                </Stack>
            ),
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            disableColumnMenu: true,
            display: 'flex'
        },
    ];

    const largeScreenColumns: GridColDef[] = [
        {
            field: 'id',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant="body2" sx={{fontFamily: 'LemonMilk'}}>
                    {'Date'}
                </Typography>
            ),            
            flex: 1,
            valueFormatter: (value: string) => {
                const formattedDate = new Date(value).toLocaleDateString();
                const formattedTime = new Date(value).toLocaleTimeString([], {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                });
                const formattedDateTime = `${formattedDate} @ ${formattedTime}`;
                return formattedDateTime;
            },
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            disableColumnMenu: true,
            display: 'flex'
        },
        {
            field: 'venueName',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant="body2" sx={{fontFamily: 'LemonMilk'}}>
                    {'Venue'}
                </Typography>
            ),            
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            disableColumnMenu: true,
            display: 'flex'
        },
        {
            field: 'venueLocation',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant="body2" sx={{fontFamily: 'LemonMilk'}}>
                    {'Location'}
                </Typography>
            ),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            disableColumnMenu: true,
            display: 'flex'
        },
        {
            field: 'links',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant="body2" sx={{fontFamily: 'LemonMilk'}}>
                    {'Links'}
                </Typography>
            ),
            flex: 1,
            renderCell: (params: GridRenderCellParams<any, any>) => (
                <Stack spacing={1} alignItems='center'>
                    {params.row.ticketLink && (
                        <Button
                            variant="contained"
                            onClick={() => window.open(params.row.ticketLink, '_blank')}
                            startIcon={<LocalActivity />}
                            sx={{
                                width: 150
                            }}
                        >
                            Tickets
                        </Button>
                    )}
                    {params.row.rsvpLink && (
                        <Button
                            variant="contained"
                            onClick={() =>
                                window.open(
                                    `https://bandsintown.com/artist-rsvp/2619661?event_id=${params.row.rsvpLink}`,
                                    '_blank'
                                )
                            }
                            startIcon={<NotificationsActive />}
                            sx={{
                                width: 150
                            }}
                        >
                            Remind Me
                        </Button>
                    )}
                </Stack>
            ),
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            disableColumnMenu: true,
            display: 'flex'
        },
    ];

    const columns = isSmallScreen ? smallScreenColumns : largeScreenColumns;

    useEffect(() => {
        if (!request.isSent()) {
            request.onStart();
            api.getEvents()
                .then((response) => {
                    const newRows = response.data.map((bandsInTownEvent, i) => ({
                        id: bandsInTownEvent.datetime,
                        venueName: bandsInTownEvent.venue.name,
                        venueLocation: bandsInTownEvent.venue.location,
                        rsvpLink: bandsInTownEvent.id,
                        ticketLink: bandsInTownEvent.offers.find(
                            (offer) => offer.type === 'Tickets',
                        )?.url,
                    }));
                    setRows(newRows);
                    request.onSuccess(response.data);
                })
                .catch((err) => {
                    console.error(err);
                    request.onFailure(err);
                });
        }
    }, [request, api]);

    if (auth.status === AuthStatus.Loading) {
        return <LoadingPage />;
    }

    if (!request.isSent() || request.isLoading()) {
        return <LoadingPage />;
    }

    if (!request.data) {
        return (
            <Stack>
                <RequestSnackbar request={request} />
            </Stack>
        );
    }

    if (request.data.length === 0) {
        return (
            <Container maxWidth='md' sx={{ pt: 6, mb: '100px' }}>
                <Stack spacing={2} alignItems='center' mb={8}>
                    <Typography variant='h4'>No upcoming shows</Typography>
                    <Typography variant='h6' sx={{ textAlign: 'center' }}>
                        Get updates on new shows, music, and more.
                    </Typography>
                    <Button
                        variant='contained'
                        component={Link}
                        to={`https://bandsintown.com/artist-subscribe/${config.artist.bandsInTownId}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        startIcon={<img src={BandsInTownImage} alt='Icon' />}
                    >
                        Follow
                    </Button>
                </Stack>
                <SocialBar />
            </Container>
        );
    }

    return (
        <Container maxWidth='md' sx={{ pt: 6, mb: '100px' }}>
            <Box
                sx={{
                    display: 'flex',
                }}
            >
                <DataGrid
                    rows={rows}
                    getRowHeight={() => 'auto'}
                    columns={columns}
                    hideFooter={true}
                    sx={{
                        '&.MuiDataGrid-root': {
                            bgcolor: `${theme.palette.secondary.light}`,
                        },
                        '& .MuiDataGrid-columnHeader': {
                            backgroundColor: `${theme.palette.primary.main}`,
                        },
                        '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
                            outline: 'none',
                        },
                        '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
                            py: '8px',
                        },
                        '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
                            py: '15px',
                        },
                        '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
                            py: '22px',
                        },
                    }}
                />
            </Box>
            <Stack
                spacing={2}
                justifyContent='center'
                alignItems='center'
                sx={{ mt: 8, mb: 8 }}
            >
                <Typography variant='body1' align='center'>
                    Get notified when new events are announced in your area.
                </Typography>
                <Button
                    variant='contained'
                    component={Link}
                    to={`https://bandsintown.com/artist-subscribe/${config.artist.bandsInTownId}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    startIcon={<img src={BandsInTownImage} alt='Icon' />}
                    sx={{
                        width: '150px',
                    }}
                >
                    Follow
                </Button>
            </Stack>
            <SocialBar />
        </Container>
    );
};

export default EventsPage;
